import {
	Tabs,
	Tab,
	Button,
	FormGroup,
	FormControl,
	InputGroup,
	ControlLabel,
	HelpBlock,
	Alert,
	Well,
	Glyphicon,
} from "react-bootstrap"
import React, { Component } from "react"
import KeyInputConfiguration from "./forms/KeyInputConfiguration"
import * as firebase from "firebase"
import request from "./request"
import RefreshStatus from "./RefreshStatus"
import { signInAnonymously } from "./user"
import { deleteCollection } from "./misc/firestoreUtil"

export default class Accounts extends Component {
	constructor(props) {
		super(props)
		this.state = {
			accounts: {},
			isLoading: {},
			isDeleting: {},
			isSubmittingBittrexOrderCSV: false,
			isSubmittingBitfinexOrderCSV: false,
			error: null,
			refreshStatus: null,
		}
		this.listener = null
		this.bittrexFileInput = null
		this.bitfinexFileInput = null
		this.binanceDepositFileInput = null
		this.binanceWithdrawalFileInput = null
		this.binanceTradeFileInput = null
	}

	static defaultProps = {
		formConfiguration: {
			bittrexConfiguration: [
				{
					id: "bittrexAPIKey",
					ref: "bittrexAPIKey",
					label: "Bittrex API Key",
				},
				{
					id: "bittrexAPISecret",
					ref: "bittrexAPISecret",
					label: "Bittrex API Secret",
				},
			],
			krakenConfiguration: [
				{
					id: "krakenAPIKey",
					ref: "krakenAPIKey",
					label: "Kraken API Key",
				},
				{
					id: "krakenAPISecret",
					ref: "krakenAPISecret",
					label: "Kraken Private Key",
				},
			],
			gdaxConfiguration: [
				{
					id: "gdaxAPIPassphrase",
					ref: "gdaxAPIPassphrase",
					label: "GDAX API Passphrase",
				},
				{
					id: "gdaxAPIKey",
					ref: "gdaxAPIKey",
					label: "GDAX API Key",
				},
				{
					id: "gdaxAPISecret",
					ref: "gdaxAPISecret",
					label: "GDAX API Secret",
				},
			],
			poloniexConfiguration: [
				{
					id: "poloniexAPIKey",
					ref: "poloniexAPIKey",
					label: "Poloniex API Key",
				},
				{
					id: "poloniexAPISecret",
					ref: "poloniexAPISecret",
					label: "Poloniex Secret",
				},
			],
			bitfinexConfiguration: [
				{
					id: "bitfinexAPIKey",
					ref: "bitfinexAPIKey",
					label: "Bitfinex API Key",
				},
				{
					id: "bitfinexAPISecret",
					ref: "bitfinexAPISecret",
					label: "Bitfinex Secret",
				},
			],
			binance3Configuration: [
				{
					id: "binance3APIKey",
					ref: "binance3APIKey",
					label: "Binance API Key",
				},
				{
					id: "binance3APISecret",
					ref: "binance3APISecret",
					label: "Binance Secret",
				},
			],
			kucoinConfiguration: [
				{
					id: "kucoinAPIKey",
					ref: "kucoinAPIKey",
					label: "Kucoin API Key",
				},
				{
					id: "kucoinAPISecret",
					ref: "kucoinAPISecret",
					label: "Kucoin Secret",
				},
			],
		},
	}

	componentDidMount() {
		this.updateAccountListener()
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.uid !== this.props.uid) {
			this.updateAccountListener()
		}
	}

	componentWillUnmount() {
		if (this.listener !== null) {
			this.listener() // unsubscribe
		}
	}

	updateAccountListener() {
		if (this.listener !== null) {
			this.listener() // unsubscribe
		}
		if (this.props.uid === null) {
			this.setState({
				accounts: {},
			})
			return
		}
		const db = firebase.firestore()
		this.listener = db
			.collection("accounts")
			.doc(this.props.uid)
			.onSnapshot(doc => {
				if (!doc.exists) {
					this.setState({
						accounts: {},
					})
					return
				}
				this.setState({
					accounts: doc.data(),
				})
			})
	}

	render() {
		const { accounts, isLoading, error, refreshStatus } = this.state
		const { uid } = this.props
		return (
			<div>
				{!uid ? (
					<Alert bsStyle="info">
						Get started by connecting an exchange account below.
					</Alert>
				) : isAnonymous() ? (
					<Alert bsStyle="info">
						Welcome! You can now optionally <b>create a user account</b> by
						clicking on <i>Anonymous User</i> at the top right.
					</Alert>
				) : null}
				{error ? <Alert bsStyle="danger">{error}</Alert> : null}
				{refreshStatus ? (
					<RefreshStatus
						{...refreshStatus}
						onRefresh={() =>
							this.refreshPortfolios(uid, { force: true, doFullUpdate: true })
						}
					/>
				) : null}
				<Tabs defaultActiveKey={0} id="accounts-tab">
					<Tab eventKey={0} title="Kraken">
						<ol>
							<li>
								Connect to Kraken by creating a{" "}
								<a
									href="https://www.kraken.com"
									target="_blank"
									rel="noopener noreferrer"
								>
									Kraken API key
								</a>{" "}
								at <b>Settings</b> > <b>API</b> > <b>Generate New Key</b>.
							</li>
							<li>
								Set <b>Nonce Window</b> to <b>1000000</b>.
							</li>
							<li>
								Enable the <b>Query Funds</b>, <b>Query Open Orders & Trades</b>,{" "}
								<b>Query Closed Orders & Trades</b>, and{" "}
								<b>Query Ledger Entries</b> permissions.
							</li>
							<li>
								Click <b>Generate Key</b>.
							</li>
						</ol>
						{accounts.kraken ? (
							this.renderAccountInfo(
								"kraken",
								"Kraken",
								accounts.kraken.auth.key
							)
						) : (
							<form>
								<KeyInputConfiguration
									ref="krakenConfiguration"
									formFields={this.props.formConfiguration.krakenConfiguration}
								/>
								<Button
									type="submit"
									disabled={isLoading["kraken"]}
									onClick={this.handleKrakenSubmit}
								>
									{isLoading["kraken"]
										? "Connecting to Kraken..."
										: "Connect to Kraken"}
								</Button>
							</form>
						)}
					</Tab>
					<Tab eventKey={1} title="Bittrex">
						<ol>
							<li>
								{" "}
								Connect to Bittrex by creating a{" "}
								<a
									href="https://bittrex.com/Manage#sectionApi"
									target="_blank"
									rel="noopener noreferrer"
								>
									Bittrex API key
								</a>{" "}
								with <b>Read Info</b> permission.
							</li>
							<li>
								On your{" "}
								<a
									href="https://bittrex.com/History"
									target="_blank"
									rel="noopener noreferrer"
								>
									orders page
								</a>
								, click <b>Load All</b> to get your <b>fullOrders.csv</b> file.
							</li>
						</ol>
						{accounts.bittrex ? (
							this.renderAccountInfo(
								"bittrex",
								"Bittrex",
								accounts.bittrex.auth.key
							)
						) : (
							<form>
								<KeyInputConfiguration
									ref="bittrexConfiguration"
									formFields={this.props.formConfiguration.bittrexConfiguration}
								/>
								<FieldGroup
									id="bittrex-csv"
									type="file"
									label="Bittrex fullOrders.csv"
									inputRef={ref => (this.bittrexFileInput = ref)}
									accept=".csv"
								/>
								<Button
									type="submit"
									disabled={isLoading["bittrex"]}
									onClick={this.handleBittrexSubmit}
								>
									{isLoading["bittrex"]
										? "Connecting to Bittrex..."
										: "Connect to Bittrex"}
								</Button>
							</form>
						)}
					</Tab>
					<Tab eventKey={3} title="GDAX">
						<p>
							Connect to GDAX by creating a{" "}
							<a
								href="https://www.gdax.com/settings/api"
								target="_blank"
								rel="noopener noreferrer"
							>
								GDAX API key
							</a>{" "}
							with <b>View</b> permission.
						</p>
						{accounts.gdax ? (
							this.renderAccountInfo("gdax", "GDAX", accounts.gdax.auth.key)
						) : (
							<form>
								<KeyInputConfiguration
									ref="gdaxConfiguration"
									formFields={this.props.formConfiguration.gdaxConfiguration}
								/>
								<Button
									type="submit"
									disabled={isLoading["gdax"]}
									onClick={this.handleGDAXSubmit}
								>
									{isLoading["gdax"]
										? "Connecting to GDAX..."
										: "Connect to GDAX"}
								</Button>
							</form>
						)}
					</Tab>
					<Tab eventKey={4} title="Poloniex">
						<ol>
							<li>
								Connect to Poloniex by creating a new{" "}
								<a
									href="https://poloniex.com/apiKeys"
									target="_blank"
									rel="noopener noreferrer"
								>
									Poloniex API key
								</a>.
							</li>
							<li>
								<b>Uncheck Enable Trading</b>.
							</li>
							<li>
								Leave <b>IP Access Restriction</b> at{" "}
								<b>Unrestricted (Less Secure)</b>.
							</li>
							<li>
								Click <b>Show</b> in the <b>Secret</b> field to see your
								Poloniex Secret.
							</li>
						</ol>
						{accounts.poloniex ? (
							this.renderAccountInfo(
								"poloniex",
								"Poloniex",
								accounts.poloniex.auth.key
							)
						) : (
							<form>
								<KeyInputConfiguration
									ref="poloniexConfiguration"
									formFields={
										this.props.formConfiguration.poloniexConfiguration
									}
								/>
								<Button
									type="submit"
									disabled={isLoading["poloniex"]}
									onClick={this.handlePoloniexSubmit}
								>
									{isLoading["poloniex"]
										? "Connecting to Poloniex..."
										: "Connect to Poloniex"}
								</Button>
							</form>
						)}
					</Tab>
					<Tab eventKey={5} title="Bitfinex">
						<ol>
							<li>
								Connect to Bitfinex by creating a new{" "}
								<a
									href="https://www.bitfinex.com/api"
									target="_blank"
									rel="noopener noreferrer"
								>
									Bitfinex API key
								</a>.
							</li>
							<li>
								Leave all <b>Read</b> permissions checked, click{" "}
								<b>Generate API key</b>, and enter the key and secret
								information below.
							</li>
							<li>
								Go to your{" "}
								<a
									href="https://www.bitfinex.com/reports"
									target="_blank"
									rel="noopener noreferrer"
								>
									Reports
								</a>{" "}
								page and click <b>Trade History</b>. Select <b>Past 3 months</b>,
								click <b>Download</b>, and submit the file below. If your trade
								history is longer than 3 months, you can submit additional files
								later.
							</li>
						</ol>
						{accounts.bitfinex ? (
							this.renderAccountInfo(
								"bitfinex",
								"Bitfinex",
								accounts.bitfinex.auth.key
							)
						) : (
							<form>
								<KeyInputConfiguration
									ref="bitfinexConfiguration"
									formFields={
										this.props.formConfiguration.bitfinexConfiguration
									}
								/>
								<FieldGroup
									id="bitfinex-csv"
									type="file"
									label="Bitfinex *-trades.csv"
									inputRef={ref => (this.bitfinexFileInput = ref)}
									accept=".csv"
								/>
								<Button
									type="submit"
									disabled={isLoading["bitfinex"]}
									onClick={this.handleBitfinexSubmit}
								>
									{isLoading["bitfinex"]
										? "Connecting to Bitfinex..."
										: "Connect to Bitfinex"}
								</Button>
							</form>
						)}
					</Tab>
					<Tab eventKey={6} title="Binance">
						<ol>
							<li>
								Connect to Binance by creating a{" "}
								<a
									href="https://www.binance.com/userCenter/createApi.html"
									target="_blank"
									rel="noopener noreferrer"
								>
									Binance API key
								</a>. You may label the key <b>Crypto Portfolio Tracker</b>.
							</li>
							<li>
								Enter the <b>API Key</b> and <b>Secret</b> in the form below.
							</li>
							<li>
								In the created key, click <b>Edit</b>, uncheck{" "}
								<b>Enable Trading</b>, and click <b>Save</b>.
							</li>
						</ol>
						{accounts.binance3 ? (
							this.renderAccountInfo(
								"binance3",
								"Binance",
								accounts.binance3.auth.key
							)
						) : (
							<form>
								<KeyInputConfiguration
									ref="binance3Configuration"
									formFields={
										this.props.formConfiguration.binance3Configuration
									}
								/>
								<Button
									type="submit"
									disabled={isLoading["binance3"]}
									onClick={this.handleBinance3Submit}
								>
									{isLoading["binance3"]
										? "Connecting to Binance..."
										: "Connect to Binance"}
								</Button>
							</form>
						)}
					</Tab>
					<Tab eventKey={7} title="Kucoin">
						<Alert bsStyle="danger">
							<b>All Kucoin API keys have trade and withdrawal permission.</b>{" "}
							While we do take measures to protect your API keys, Crypto
							Portfolio Tracker will not be held responsible for any activity
							whatsoever resulting from your submitted Kucoin API key.
						</Alert>
						<Alert>
							THIS SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
							EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
							MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
							NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
							HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
							WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
							OUT OF OR IN CONNECTION WITH THIS SOFTWARE OR THE USE OR OTHER
							DEALINGS IN THIS SOFTWARE.
						</Alert>
						{accounts.kucoin ? (
							this.renderAccountInfo(
								"kucoin",
								"Kucoin",
								accounts.kucoin.auth.key
							)
						) : (
							<form>
								<KeyInputConfiguration
									ref="kucoinConfiguration"
									formFields={this.props.formConfiguration.kucoinConfiguration}
								/>
								<Button
									type="submit"
									disabled={isLoading["kucoin"]}
									onClick={this.handleKucoinSubmit}
								>
									{isLoading["kucoin"]
										? "Connecting to Kucoin..."
										: "Connect to Kucoin"}
								</Button>
							</form>
						)}
					</Tab>
				</Tabs>
			</div>
		)
	}

	handleBittrexSubmit = async e => {
		e.preventDefault()
		const data = this.refs.bittrexConfiguration.getData()
		await this.connectAccount("bittrex", {
			type: "bittrex",
			name: "Bittrex",
			auth: {
				key: data["bittrexAPIKey"],
				secret: data["bittrexAPISecret"],
			},
		})
	}

	handleKrakenSubmit = async e => {
		e.preventDefault()
		const data = this.refs.krakenConfiguration.getData()
		this.connectAccount("kraken", {
			type: "kraken",
			name: "Kraken",
			auth: {
				key: data["krakenAPIKey"],
				secret: data["krakenAPISecret"],
			},
		})
	}

	handleGDAXSubmit = async e => {
		e.preventDefault()
		const data = this.refs.gdaxConfiguration.getData()
		this.connectAccount("gdax", {
			type: "gdax",
			name: "GDAX",
			auth: {
				key: data["gdaxAPIKey"],
				secret: data["gdaxAPISecret"],
				passphrase: data["gdaxAPIPassphrase"],
			},
		})
	}

	handlePoloniexSubmit = async e => {
		e.preventDefault()
		const data = this.refs.poloniexConfiguration.getData()
		this.connectAccount("poloniex", {
			type: "poloniex",
			name: "Poloniex",
			auth: {
				key: data["poloniexAPIKey"],
				secret: data["poloniexAPISecret"],
			},
		})
	}

	handleBitfinexSubmit = async e => {
		e.preventDefault()
		const data = this.refs.bitfinexConfiguration.getData()
		this.connectAccount("bitfinex", {
			type: "bitfinex",
			name: "Bitfinex",
			auth: {
				key: data["bitfinexAPIKey"],
				secret: data["bitfinexAPISecret"],
			},
		})
	}

	handleBinance3Submit = async e => {
		e.preventDefault()
		const data = this.refs.binance3Configuration.getData()
		this.connectAccount("binance3", {
			type: "binance3",
			name: "Binance",
			auth: {
				key: data["binance3APIKey"],
				secret: data["binance3APISecret"],
			},
		})
	}

	handleKucoinSubmit = async e => {
		e.preventDefault()
		const data = this.refs.kucoinConfiguration.getData()
		this.connectAccount("kucoin", {
			type: "kucoin",
			name: "Kucoin",
			auth: {
				key: data["kucoinAPIKey"],
				secret: data["kucoinAPISecret"],
			},
		})
	}

	async connectAccount(id, payload) {
		this.setState((prevState, props) => ({
			isLoading: Object.assign({}, prevState.isLoading, { [id]: true }),
		}))
		let err = null
		try {
			const currentUser = await getCurrentUser()
			const promises = []
			const db = firebase.firestore()
			const account = {
				[payload.type]: {
					auth: {
						key: payload.auth.key,
					},
					name: payload.name,
					type: payload.type,
				},
			}
			const secret = {
				[payload.type]: {
					auth: {
						secret: payload.auth.secret,
					},
				},
			}
			if (payload.auth.passphrase !== undefined) {
				secret[payload.type].auth.passphrase = payload.auth.passphrase
			}
			const batch = db.batch()
			batch.set(db.collection("accounts").doc(currentUser.uid), account, {
				merge: true,
			})
			batch.set(db.collection("secrets").doc(currentUser.uid), secret, {
				merge: true,
			})
			promises.push(batch.commit())
			if (payload.type === "bittrex") {
				promises.push(this.uploadBittrexOrderHistoryCSV(currentUser.uid))
			} else if (payload.type === "bitfinex") {
				promises.push(this.uploadBitfinexOrderHistory(currentUser.uid))
			} else if (payload.type === "binance") {
				promises.push(this.uploadBinanceHistory(currentUser.uid))
			}
			await Promise.all(promises)
			await this.refreshPortfolios(currentUser.uid, {
				force: true,
				doFullUpdate: true,
			})
		} catch (error) {
			err = error.message
			throw err
		} finally {
			this.setState((prevState, props) => ({
				error: err,
				isLoading: Object.assign({}, prevState.isLoading, { [id]: false }),
			}))
		}
	}

	uploadBittrexOrderHistoryCSV(uid) {
		const file = this.bittrexFileInput.files[0]
		if (file === undefined) {
			throw new Error("Bittrex Order History CSV file is missing")
		}
		const storage = firebase.storage()
		return storage
			.ref("bittrexOrderHistoryCSVs")
			.child(uid)
			.put(file)
	}

	uploadBitfinexOrderHistory(uid) {
		const file = this.bitfinexFileInput.files[0]
		if (file === undefined) {
			throw new Error("Bitfinex Trade History CSV file is missing")
		}
		const storage = firebase.storage()
		return storage
			.ref("tradeHistory")
			.child(uid)
			.child("bitfinex.csv")
			.put(file)
	}

	uploadBinanceHistory(uid) {
		const depositFile = this.binanceDepositFileInput.files[0]
		if (depositFile === undefined) {
			throw new Error("Binance Deposit file is missing")
		}
		const withdrawalFile = this.binanceWithdrawalFileInput.files[0]
		if (withdrawalFile === undefined) {
			throw new Error("Binance Withdrawal file is missing")
		}
		const tradeFile = this.binanceTradeFileInput.files[0]
		if (tradeFile === undefined) {
			throw new Error("Binance Trade file is missing")
		}
		const storage = firebase.storage()
		const ref = storage.ref("tradeHistory").child(uid)
		return Promise.all([
			ref.child("binance-deposit.xlsx").put(depositFile),
			ref.child("binance-withdrawal.xlsx").put(withdrawalFile),
			ref.child("binance-trade.xlsx").put(tradeFile),
		])
	}

	renderAccountInfo(id, name, key) {
		return (
			<form>
				<FormGroup>
					<InputGroup>
						<InputGroup.Addon>{name} API Key</InputGroup.Addon>
						<FormControl type="text" disabled value={key} />
					</InputGroup>
				</FormGroup>
				{id === "bittrex" ? (
					<div>
						<Well>
							<div>
								Getting <b>APIKEY_INVALID</b>?
							</div>
							<div>
								Bittrex may take up to a minute to validate new API keys. Try
								refreshing your portfolio after a minute.<br />
								If this error message still appears, and you're sure that your
								API key is valid, please contact us using chat for help.
							</div>
						</Well>
						<Well>
							<p>
								<Glyphicon glyph="ok" /> You've submitted your{" "}
								<b>fullOrders.csv</b> file. Only use this form to submit a new
								file if there are missing transactions.
							</p>
							<FieldGroup
								id="bittrex-csv"
								type="file"
								label="Bittrex fullOrders.csv"
								inputRef={ref => (this.bittrexFileInput = ref)}
								accept=".csv"
							/>
							<Button
								type="submit"
								disabled={this.state.isSubmittingBittrexOrderCSV}
								onClick={this.handleBittrexOrderCSVSubmit}
							>
								{this.state.isSubmittingBittrexOrderCSV
									? "Submitting Bittrex Order History CSV..."
									: "Submit Bittrex Order History CSV"}
							</Button>
						</Well>
					</div>
				) : null}
				{id === "bitfinex" ? (
					<div>
						<Well>
							<p>
								<Glyphicon glyph="ok" /> You've submitted your{" "}
								<b>*-trades.csv</b> file. Only use this form to submit a new
								file if you have additional history to import or if there are
								missing transactions.
							</p>
							<FieldGroup
								id="bitfinex-csv"
								type="file"
								label="Bitfinex *-trades.csv"
								inputRef={ref => (this.bitfinexFileInput = ref)}
								accept=".csv"
							/>
							<Button
								type="submit"
								disabled={this.state.isSubmittingBitfinexOrderCSV}
								onClick={this.handleBitfinexOrderCSVSubmit}
							>
								{this.state.isSubmittingBitfinexOrderCSV
									? "Submitting Bitfinex Trade History CSV..."
									: "Submit Bitfinex Trade History CSV"}
							</Button>
						</Well>
					</div>
				) : null}
				<Button
					bsStyle="danger"
					disabled={this.state.isDeleting[id]}
					onClick={this.deleteAccountHandler(id)}
				>
					{this.state.isDeleting[id]
						? `Deleting ${name}... (may take a minute)`
						: `Delete ${name} Account`}
				</Button>
			</form>
		)
	}

	handleBittrexOrderCSVSubmit = async e => {
		e.preventDefault()
		try {
			this.setState({
				isSubmittingBittrexOrderCSV: true,
			})
			await this.uploadBittrexOrderHistoryCSV(this.props.uid)
			await this.refreshPortfolios(this.props.uid, {
				force: true,
				doFullUpdate: true,
			})
		} catch (error) {
			this.setState((prevState, props) => ({
				error,
			}))
		} finally {
			this.setState({
				isSubmittingBittrexOrderCSV: false,
			})
		}
	}

	handleBitfinexOrderCSVSubmit = async e => {
		e.preventDefault()
		try {
			this.setState({
				isSubmittingBitfinexOrderCSV: true,
			})
			await this.uploadBitfinexOrderHistory(this.props.uid)
			await this.refreshPortfolios(this.props.uid, {
				force: true,
				doFullUpdate: true,
			})
		} catch (error) {
			this.setState((prevState, props) => ({
				error,
			}))
		} finally {
			this.setState({
				isSubmittingBitfinexOrderCSV: false,
			})
		}
	}

	deleteAccountHandler = id => {
		return async () => {
			this.setState((prevState, props) => ({
				isDeleting: Object.assign({}, prevState.isDeleting, { [id]: true }),
			}))
			let err = null
			try {
				const currentUser = await getCurrentUser()
				const db = firebase.firestore()
				const account = {
					[id]: firebase.firestore.FieldValue.delete(),
				}
				const secret = {
					[id]: firebase.firestore.FieldValue.delete(),
				}
				await deleteCollection(
					db,
					db
						.collection("portfolios")
						.doc(currentUser.uid)
						.collection("transactions")
						.where("account", "==", id)
				)
				const batch = db.batch()
				batch.update(db.collection("accounts").doc(currentUser.uid), account)
				batch.update(db.collection("secrets").doc(currentUser.uid), secret)
				await batch.commit()
				await this.refreshPortfolios(currentUser.uid)
			} catch (error) {
				console.error(error)
				err = error.message
			} finally {
				this.setState((prevState, props) => ({
					error: err,
					isDeleting: Object.assign({}, prevState.isDeleting, { [id]: false }),
				}))
			}
		}
	}

	refreshPortfolios = (uid, { force = false, doFullUpdate = false } = {}) => {
		return request(
			uid,
			{
				action: "refreshPortfolios",
				payload: {
					force,
					doFullUpdate,
				},
			},
			data => {
				this.setState({
					refreshStatus: data,
				})
			}
		)
	}
}

function getCurrentUser() {
	const auth = firebase.auth()
	if (auth.currentUser === null) {
		return signInAnonymously()
	}
	return Promise.resolve(auth.currentUser)
}

function isAnonymous() {
	const auth = firebase.auth()
	if (auth.currentUser) {
		return auth.currentUser.isAnonymous
	} else {
		return false
	}
}

function FieldGroup({ id, label, help, ...props }) {
	return (
		<FormGroup controlId={id}>
			<ControlLabel>{label}</ControlLabel>
			<FormControl {...props} />
			{help && <HelpBlock>{help}</HelpBlock>}
		</FormGroup>
	)
}



// WEBPACK FOOTER //
// ./src/Accounts.js